import React from 'react';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { ReactComponent as VerticalDots } from 'assets/vertical-dots.svg';
import zeplinColor from '../../../theme/Color';
import Pagination from '../../shared/CustomPagination/CustomPagination';
import CustomSearch from '../../shared/CustomSearch/CustomSearch';
import CustomButton from '../../shared/CustomButton/CustomButton';
import { LightTooltip } from '../../shared/CustomTooltip/CustomTooltip';
import { useAppSelector } from '../../../store/hooks';
import { TeamService } from '../../../service/team.service';
import { useParams } from 'react-router-dom';
import TeamMetricsGrid from './TeamMetricsGrid';
import AddAssignmentModal from './AddAssignmentModal';

type AssignmentRow = {
  id: string;
  name: string;
  category: string;
  capacityWeight: number;
  activeDeals: number;
  totalTasks: number;
};

type SortField = 'name' | 'capacityWeight' | 'activeDeals' | 'totalTasks';
type SortOrder = 'asc' | 'desc';

const TeamAssignmentsTab = () => {
  const selectedMember = useAppSelector((state) => state.teamUi.selectedMember) || {};
  const params = useParams();

  const [addAssignmentOpen, setAddAssignmentOpen] = React.useState(false);
  const [rows, setRows] = React.useState<AssignmentRow[]>([]);
  const [workloadLoading, setWorkloadLoading] = React.useState(false);
  const [apiMetrics, setApiMetrics] = React.useState({
    workloadCapacity: 0,
    activeClients: 0,
    totalDeals: 0,
    totalTasks: 0,
  });
  const [nextToken, setNextToken] = React.useState<string | null>(null);
  const [searchInput, setSearchInput] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 2000);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchWorkload = React.useCallback(
    (memberId: string, token?: string | null, limit?: number, query?: string) => {
      setWorkloadLoading(true);
      const params: string[] = [];
      if (limit) params.push(`limit=${limit}`);
      if (token) params.push(`nextToken=${encodeURIComponent(token)}`);
      if (query) params.push(`search=${encodeURIComponent(query)}`);
      const url = params.length ? `${memberId}?${params.join('&')}` : memberId;
      TeamService.getMemberWorkload(url)
        .then((data: any) => {
          const mapped: AssignmentRow[] = (data?.assignments || []).map((item: any) => ({
            id: item.assignmentID,
            name: item.clientName,
            category: item.category,
            capacityWeight: item.capacityWeight,
            activeDeals: item.activeDeals,
            totalTasks: item.totalTasks,
          }));
          setRows((prev) => (token ? [...prev, ...mapped] : mapped));
          setNextToken(data?.nextToken ?? null);
          setApiMetrics({
            workloadCapacity: data?.totalWorkloadCapacity ?? 0,
            activeClients: data?.activeClients ?? 0,
            totalDeals: data?.totalDeals ?? 0,
            totalTasks: data?.totalTasks ?? 0,
          });
        })
        .catch(() => {
          setRows([]);
          setNextToken(null);
        })
        .finally(() => setWorkloadLoading(false));
    },
    [],
  );

  const [sortField, setSortField] = React.useState<SortField>('name');
  const [sortOrder, setSortOrder] = React.useState<SortOrder>('asc');
  const [currentPage, setCurrentPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [pageWindowStart, setPageWindowStart] = React.useState(0);

  React.useEffect(() => {
    const memberId = params.id || selectedMember?.id;
    if (!memberId) return;
    setRows([]);
    setNextToken(null);
    setCurrentPage(0);
    setPageWindowStart(0);
    fetchWorkload(memberId, null, rowsPerPage, debouncedSearch);
  }, [params.id, selectedMember?.id, rowsPerPage, debouncedSearch, fetchWorkload]);

  const sortedRows = React.useMemo(() => {
    const items = [...rows];

    items.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }

      return sortOrder === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });

    return items;
  }, [rows, sortField, sortOrder]);

  const paginatedRows = React.useMemo(() => {
    const start = currentPage * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, currentPage, rowsPerPage]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / rowsPerPage));

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortField(field);
    setSortOrder('asc');
  };

  const metrics = apiMetrics;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <Box sx={{ width: { xs: '100%', xl: '30rem' } }}>
          <CustomSearch
            getProjects={(value: string) => {
              setSearchInput(value);
              setCurrentPage(0);
              setPageWindowStart(0);
            }}
          />
        </Box>

        <LightTooltip
          title={
            <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
              New Assignment
            </Typography>
          }
          placement='bottom-end'
          style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
        >
          <CustomButton
            disabled={false}
            onClick={() => setAddAssignmentOpen(true)}
            variant='contained'
            color={'primary'}
            buttonVariant='responsiveButtonLarge'
            style={{
              width: 'fit-content',
              borderRadius: { xs: '10px', xl: '14px' },
              height: { xs: 30, xl: 48 },
              padding: { xs: '10px 12px', xl: '14px 16px' },
              '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
            }}
            textStyle={{ color: zeplinColor.Neutral1, width: 'fit-content' }}
            text={'New Assignment'}
            iconPosition='start'
            icon={
              <img
                src='/assets/add-white.svg'
                alt='add-assignment'
                style={{ width: 14, height: 14 }}
              />
            }
          />
        </LightTooltip>
      </Box>

      <TeamMetricsGrid metrics={metrics} />

      <TableContainer sx={{ borderRadius: '14px', border: `1px solid ${zeplinColor.Neutral5}` }}>
        <Table stickyHeader sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow
              sx={{
                borderBottom: `1px solid ${zeplinColor.Neutral5}`,
                '& th': {
                  backgroundColor: zeplinColor.Neutral3,
                  color: zeplinColor.BasicBlack,
                },
                '& th:first-of-type': {
                  borderRadius: '14px 0px 0px 14px',
                },
                '& th:last-child': {
                  borderRadius: '0px 14px 14px 0px',
                },
              }}
            >
              <TableCell sx={{ p: '12px 16px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography variant='responsiveBody3'>Name</Typography>
                  <IconButton size='small' onClick={() => handleSort('name')} sx={{ p: '2px' }}>
                    <UnfoldMoreIcon sx={{ fontSize: 16, color: zeplinColor.BasicBlack }} />
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell sx={{ p: '12px 16px' }}>
                <Typography variant='responsiveBody3'>Category</Typography>
              </TableCell>
              <TableCell sx={{ p: '12px 16px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography variant='responsiveBody3'>Capacity Weight</Typography>
                  <IconButton
                    size='small'
                    onClick={() => handleSort('capacityWeight')}
                    sx={{ p: '2px' }}
                  >
                    <UnfoldMoreIcon sx={{ fontSize: 16, color: zeplinColor.BasicBlack }} />
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell sx={{ p: '12px 16px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography variant='responsiveBody3'>Active Deals</Typography>
                  <IconButton
                    size='small'
                    onClick={() => handleSort('activeDeals')}
                    sx={{ p: '2px' }}
                  >
                    <UnfoldMoreIcon sx={{ fontSize: 16, color: zeplinColor.BasicBlack }} />
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell sx={{ p: '12px 16px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography variant='responsiveBody3'>Total Tasks</Typography>
                  <IconButton
                    size='small'
                    onClick={() => handleSort('totalTasks')}
                    sx={{ p: '2px' }}
                  >
                    <UnfoldMoreIcon sx={{ fontSize: 16, color: zeplinColor.BasicBlack }} />
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell align='center' sx={{ p: '12px 16px', width: 48 }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedRows.length === 0 && !workloadLoading && (
              <TableRow>
                <TableCell colSpan={6} sx={{ border: 'none', p: '48px 16px' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      justifyContent: 'center',
                      alignItems: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <img
                      src={'/assets/no-chart.svg'}
                      width={'36px'}
                      height={'36px'}
                      alt='no-data'
                    />
                    <Typography variant='responsiveHeading3'>No Data Available</Typography>
                    <Typography variant='responsiveBody3' color={zeplinColor.Neutral8}>
                      There's no data to display right now. Data will appear here automatically once
                      available.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {paginatedRows.map((item) => (
              <TableRow
                key={item.id}
                sx={{
                  borderBottom: `1px solid ${zeplinColor.Neutral5}`,
                  '&:hover': { backgroundColor: zeplinColor.Neutral2 },
                }}
              >
                <TableCell sx={{ p: '12px 16px' }}>
                  <Typography variant='body3' color={zeplinColor.BasicBlack}>
                    {item.name}
                  </Typography>
                </TableCell>
                <TableCell sx={{ p: '12px 16px' }}>
                  <Typography variant='body3' color={zeplinColor.BasicBlack}>
                    {item.category}
                  </Typography>
                </TableCell>
                <TableCell sx={{ p: '12px 16px' }}>
                  <Typography variant='body3' color={zeplinColor.BasicBlack}>
                    {item.capacityWeight}%
                  </Typography>
                </TableCell>
                <TableCell sx={{ p: '12px 16px' }}>
                  <Typography variant='body3' color={zeplinColor.Brand1}>
                    {item.activeDeals}
                  </Typography>
                </TableCell>
                <TableCell sx={{ p: '12px 16px' }}>
                  <Typography variant='body3' color={zeplinColor.Brand1}>
                    {item.totalTasks}
                  </Typography>
                </TableCell>
                <TableCell align='center' sx={{ p: '12px 16px' }}>
                  <VerticalDots style={{ width: 20, height: 20, cursor: 'pointer' }} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Pagination
        setCurrentPage={setCurrentPage}
        setRowsPerPage={setRowsPerPage}
        currentPage={currentPage}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        count={sortedRows.length}
        paginatedDataLength={paginatedRows.length}
        onFirstPage={() => {
          setCurrentPage(0);
          setPageWindowStart(0);
        }}
        onPreviousPage={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
        onNextPage={() => {
          const nextPageIndex = currentPage + 1;
          const isLastLoadedPage = (nextPageIndex + 1) * rowsPerPage >= rows.length;
          if (isLastLoadedPage && nextToken) {
            const memberId = params.id || selectedMember?.id;
            if (memberId) fetchWorkload(memberId, nextToken, rowsPerPage, debouncedSearch);
          }
          setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1));
        }}
        onLastPage={() => {
          const last = Math.max(totalPages - 1, 0);
          setCurrentPage(last);
          setPageWindowStart(Math.max(0, totalPages - 4));
        }}
        onPageChange={(page) => setCurrentPage(page)}
        onRowsPerPageChange={(value) => {
          setRowsPerPage(value);
          setCurrentPage(0);
          setPageWindowStart(0);
        }}
        pageWindowStart={pageWindowStart}
        setPageWindowStart={setPageWindowStart}
      />

      <AddAssignmentModal
        open={addAssignmentOpen}
        onClose={() => setAddAssignmentOpen(false)}
        currentCapacity={apiMetrics.workloadCapacity}
      />
    </Box>
  );
};

export default TeamAssignmentsTab;
